import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Input, message, Modal, Space } from 'antd';
import {
  FormAnswerRestriction,
  FormTemplate,
} from '../../models/TaskForm/TaskForm';
import {
  createFormTemplate,
  getTaskFormAnswerRestrictions,
  selectTaskFormAnswerRestrictionOptions,
  selectTaskFormAnswerRestrictionsStatus,
  selectTaskFormManagementTemplates,
} from '../../store/reducers/TaskFormSlice';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { AnswerRestrictionOption } from './AnswerRestrictionSelect';
import {
  TemplateQuestionList,
  TemplateQuestionListHandle,
} from './TemplateQuestionList';
import {
  createEmptyTemplateQuestion,
  getEditableTemplateQuestions,
  useTemplateQuestions,
} from './useTemplateQuestions';
import { toCreateFormTemplateRequest } from './templatePayload';
import styles from './templateEditorModal.module.css';

const fallbackAnswerRestrictionOptions: AnswerRestrictionOption[] = [
  { value: FormAnswerRestriction.YesNo, label: 'Да / Нет' },
  { value: FormAnswerRestriction.Number_1_5, label: 'Шкала (5-балльная)' },
  { value: FormAnswerRestriction.Number_1_10, label: 'Шкала (10-балльная)' },
  { value: FormAnswerRestriction.Number, label: 'Число' },
  { value: FormAnswerRestriction.Text, label: 'Свободный ответ' },
  { value: FormAnswerRestriction.Date, label: 'Дата' },
];

interface Props {
  open: boolean;
  template: FormTemplate | null;
  onClose: () => void;
  onSaved?: () => void;
}

const initialTemplateName = 'Новая анкета';

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const errorMessage = (error as { message?: unknown }).message;
    if (typeof errorMessage === 'string') return errorMessage;
  }

  return 'Не удалось сохранить шаблон анкеты';
};

export const TemplateEditorModal: FC<Props> = ({
  open,
  template,
  onClose,
  onSaved,
}) => {
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectTaskFormManagementTemplates);
  const answerRestrictionOptions = useAppSelector(
    selectTaskFormAnswerRestrictionOptions,
  );
  const answerRestrictionsStatus = useAppSelector(
    selectTaskFormAnswerRestrictionsStatus,
  );

  const isNew = template === null;
  const readonly = !isNew;
  const questionListRef = useRef<TemplateQuestionListHandle>(null);

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [answerRestrictionsRequested, setAnswerRestrictionsRequested] =
    useState(false);
  const {
    questions,
    replaceQuestions,
    addQuestion,
    updateQuestion,
    moveQuestion,
    reorderQuestions,
    copyQuestion,
    deleteQuestion,
  } = useTemplateQuestions({ readonly });

  const isDirtyCreate = useMemo(() => {
    if (!isNew) return false;

    const defaultQuestion = createEmptyTemplateQuestion();

    return (
      name !== initialTemplateName ||
      questions.length !== 1 ||
      questions[0]?.question !== defaultQuestion.question ||
      questions[0]?.restriction !== defaultQuestion.restriction
    );
  }, [isNew, name, questions]);

  const questionTextStats = useMemo(() => {
    const questionClientIdsByText = new Map<string, string[]>();
    let hasEmptyQuestion = false;

    questions.forEach((question) => {
      const normalizedText = question.question.trim().toLowerCase();

      if (!normalizedText) {
        hasEmptyQuestion = true;
        return;
      }

      const existingClientIds =
        questionClientIdsByText.get(normalizedText) ?? [];
      existingClientIds.push(question.clientId);
      questionClientIdsByText.set(normalizedText, existingClientIds);
    });

    const duplicateQuestionClientIds = Array.from(
      questionClientIdsByText.values(),
    )
      .filter((clientIds) => clientIds.length > 1)
      .flat();

    return {
      hasEmptyQuestion,
      duplicateQuestionClientIds,
      hasDuplicateQuestions: duplicateQuestionClientIds.length > 0,
    };
  }, [questions]);

  const isSaveDisabled =
    saving ||
    questionTextStats.hasEmptyQuestion ||
    questionTextStats.hasDuplicateQuestions;

  useEffect(() => {
    if (!open) return;

    if (isNew) {
      setName(initialTemplateName);
      replaceQuestions([createEmptyTemplateQuestion()]);
    } else if (template) {
      setName(template.name ?? '');
      replaceQuestions(getEditableTemplateQuestions(template));
    }

    setSaveError(null);
    setAnswerRestrictionsRequested(false);
  }, [open, template, isNew, replaceQuestions]);

  useEffect(() => {
    if (
      open &&
      answerRestrictionOptions.length === 0 &&
      !answerRestrictionsStatus.responseLoading &&
      !answerRestrictionsRequested
    ) {
      setAnswerRestrictionsRequested(true);
      dispatch(getTaskFormAnswerRestrictions());
    }
  }, [
    answerRestrictionOptions.length,
    answerRestrictionsRequested,
    answerRestrictionsStatus.responseLoading,
    dispatch,
    open,
  ]);

  const validate = (): boolean => {
    const trimmed = name.trim();
    if (!trimmed) {
      message.error('Название анкеты обязательно');
      return false;
    }

    const exists = templates.some(
      (t) => t.name?.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      message.error('Название анкеты должно быть уникальным');
      return false;
    }

    const empty = questions.some((q) => !q.question?.trim());
    if (empty) {
      message.error('Заполните текст всех вопросов');
      return false;
    }

    if (questionTextStats.hasDuplicateQuestions) {
      message.error('Тексты вопросов должны быть уникальными');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (saving) return;
    if (!validate()) return;

    const payload = toCreateFormTemplateRequest(name, questions);

    setSaving(true);
    setSaveError(null);

    try {
      await dispatch(createFormTemplate(payload)).unwrap();
      message.success('Шаблон анкеты сохранён');
      onSaved?.();
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setSaveError(errorMessage);
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;

    if (isDirtyCreate) {
      Modal.confirm({
        title: 'Закрыть без сохранения?',
        content: 'Несохранённые изменения в новой анкете будут потеряны.',
        okText: 'Закрыть',
        cancelText: 'Остаться',
        onOk: onClose,
      });
      return;
    }

    onClose();
  };

  const handleAddQuestion = () => {
    questionListRef.current?.addQuestionAndScroll();
  };

  const title = isNew ? 'Новая анкета' : (template?.name ?? 'Просмотр анкеты');

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      width={800}
      className={styles.templateEditorModal}
      maskClosable={!saving}
      data-testid="template-editor-modal"
      footer={
        isNew
          ? [
              <div key="create-footer" className={styles.footerActions}>
                <Button
                  data-testid="template-add-question-button"
                  icon={<PlusOutlined />}
                  type="primary"
                  className={`${styles.addQuestionButton} ${styles.footerAddQuestionButton}`}
                  disabled={saving}
                  onClick={handleAddQuestion}
                >
                  Добавить вопрос
                </Button>
                <Button
                  data-testid="template-cancel-button"
                  disabled={saving}
                  onClick={handleClose}
                >
                  Отмена
                </Button>
                <Button
                  data-testid="template-save-button"
                  type="primary"
                  loading={saving}
                  disabled={isSaveDisabled}
                  onClick={handleSave}
                >
                  Сохранить
                </Button>
              </div>,
            ]
          : [
              <div key="view-footer" className={styles.footerActions}>
                <Button data-testid="template-close-button" onClick={onClose}>
                  Закрыть
                </Button>
              </div>,
            ]
      }
      destroyOnHidden
    >
      <Space direction="vertical" className={styles.modalContent} size="middle">
        {saveError && (
          <Alert
            type="error"
            showIcon
            message="Не удалось сохранить шаблон"
            description={saveError}
            data-testid="template-save-error"
          />
        )}
        <Input
          data-testid="template-name-input"
          value={name}
          disabled={readonly}
          onChange={(e) => setName(e.target.value)}
          placeholder="Название анкеты"
          size="large"
        />

        <TemplateQuestionList
          ref={questionListRef}
          questions={questions}
          readonly={readonly}
          answerRestrictionOptions={answerRestrictionOptions}
          fallbackAnswerRestrictionOptions={fallbackAnswerRestrictionOptions}
          duplicateQuestionClientIds={
            questionTextStats.duplicateQuestionClientIds
          }
          onAddQuestion={addQuestion}
          onUpdateQuestion={updateQuestion}
          onMoveQuestion={moveQuestion}
          onReorderQuestion={reorderQuestions}
          onCopyQuestion={copyQuestion}
          onDeleteQuestion={deleteQuestion}
        />
      </Space>
    </Modal>
  );
};

TemplateEditorModal.displayName = 'TemplateEditorModal';
